import React, { useEffect, useRef, useState } from "react";
import styles from "./ViewUserDetailsModal.module.css";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiPlus,
  FiSave,
  FiUpload,
  FiX,
  FiChevronDown,
  FiRotateCcw,
} from "react-icons/fi";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import CreateContextModal from "Components/FeatureFlip/Common/Context/CreateContextModal";
import { useGetSegmentValue } from "API/Queries/useGetSegmentValue";
import { useAddSegmentValue } from "API/Queries/useAddSegmentValue";
import { useDeleteSegmentValue } from "API/Queries/useDeleteSegmentValue";
import { useParams } from "react-router-dom";

interface ViewUserDetailsModalProps {
  selectedSegmentName: string;
  setShowSegmentModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSegmentId: string;
}

interface SelectedContext {
  id: string;
  name: string;
}

interface Context {
  id: string;
  name: string;
}

const ViewUserDetailsModal = (props: ViewUserDetailsModalProps) => {
  const { selectedSegmentName, setShowSegmentModal, selectedSegmentId } = props;
  const [inputValue, setInputValue] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id: projectId } = useParams();

  // Three separate arrays for different states
  const [savedValues, setSavedValues] = useState<string[]>([]);
  const [removedValues, setRemovedValues] = useState<string[]>([]);
  const [newValues, setNewValues] = useState<string[]>([]);

  const [duplicateValues, setDuplicateValues] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [selectedContext, setSelectedContext] =
    useState<SelectedContext | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCreateContext, setShowCreateContext] = useState(false);
  const contexts = useFeatureFlipStore((s) => s.contexts);

  const { data: segmentValue } = useGetSegmentValue(selectedSegmentId);
  const { mutateAsync: addSegmentValue } = useAddSegmentValue();
  const { mutateAsync: deleteSegmentValue } = useDeleteSegmentValue();
  const userListRef = useRef<HTMLDivElement>(null);

  // Combine all current values for display
  const contextValue = [
    ...savedValues.filter((v) => !removedValues.includes(v)),
    ...newValues,
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const addUsers = (ids: string[]) => {
    const allCurrentValues = [...savedValues, ...newValues];

    // Split the ids into those that are in removed list and those that aren't
    const removedToRestore = ids.filter((id) => removedValues.includes(id));
    const remainingIds = ids.filter((id) => !removedValues.includes(id));

    // Handle restoration of removed items
    if (removedToRestore.length) {
      setRemovedValues((prev) =>
        prev.filter((id) => !removedToRestore.includes(id))
      );
    }

    // Handle remaining ids as normal
    const duplicates = remainingIds.filter((id) =>
      allCurrentValues.includes(id)
    );
    const unique = remainingIds.filter((id) => !allCurrentValues.includes(id));

    if (duplicates.length) {
      setDuplicateValues(duplicates);
      setTimeout(() => setDuplicateValues([]), 3000);
    }

    if (unique.length) {
      setNewValues((prev) => [...prev, ...unique]);
    }

    if (removedToRestore.length || unique.length) {
      setHasUnsavedChanges(true);
    }
  };

  const handleAddUsers = (): void => {
    if (!inputValue.trim()) return;

    const newIds = inputValue
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    addUsers(newIds);

    setInputValue("");
    if (userListRef.current) {
      userListRef.current.scrollTop = userListRef.current.scrollHeight;
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idsFromFile = result
        .split(/[\n,]/) // handle comma or new-line separated
        .map((id) => id.trim())
        .filter(Boolean);

      addUsers(idsFromFile);
      e.target.value = "";
      if (userListRef.current) {
        userListRef.current.scrollTop = userListRef.current.scrollHeight;
      }
    };
    reader.readAsText(file);
  };

  const handleRemoveUser = (id: string) => {
    if (savedValues.includes(id)) {
      // If it's a saved value, add to removed list
      setRemovedValues((prev) => [...prev, id]);
    } else if (newValues.includes(id)) {
      // If it's a new value, remove from new values
      setNewValues((prev) => prev.filter((v) => v !== id));
    }
    setHasUnsavedChanges(true);
  };

  const handleModalClose = () => {
    setShowSegmentModal(false);
  };

  const handleSaveUsers = async () => {
    if (newValues.length && selectedContext?.id) {
      await addSegmentValue({
        projectId: projectId as string,
        segmentId: selectedSegmentId,
        contextId: selectedContext?.id,
        contextValues: newValues,
      });
    }

    if (removedValues.length && selectedContext?.id) {
      await deleteSegmentValue({
        projectId: projectId as string,
        segmentId: selectedSegmentId,
        contextId: selectedContext?.id,
        contextValues: removedValues,
      });
    }
    setShowSegmentModal(false);
    return;
  };

  const handleRestoreValue = (id: string) => {
    setRemovedValues((prev) => prev.filter((v) => v !== id));
    setHasUnsavedChanges(true);
  };
  const handleCreateContextClose = () => {
    setShowCreateContext(false);
  };

  const handleCreateContextSuccess = (newContext?: Context) => {
    setShowCreateContext(false);
    setSelectedContext({
      id: newContext?.id || "",
      name: newContext?.name || "",
    });
  };
  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = removedValues.length > 0 || newValues.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [removedValues, newValues]);

  // Auto-select stored context on mount
  useEffect(() => {
    if (segmentValue?.contextId && contexts?.length) {
      const storedContext = contexts.find(
        (c) => c.id === segmentValue.contextId
      );
      if (storedContext) {
        setSelectedContext({ id: storedContext.id, name: storedContext.name });
      }
    }
    if (!!segmentValue?.contextValues?.length) {
      setSavedValues(segmentValue.contextValues);
    }

    if (userListRef.current) {
      userListRef.current.scrollTop = userListRef.current.scrollHeight;
    }
  }, [contexts, segmentValue]);

  // Check if context switching should be disabled
  const isContextLocked = savedValues.length > 0;

  return (
    <>
      {showConfirmModal && (
        <div className={styles.confirmModal}>
          <div className={styles.confirmContent}>
            <div className={styles.confirmHeader}>
              <FiAlertTriangle className={styles.warningIcon} />
              <h2>Unsaved Changes</h2>
            </div>
            <p className={styles.confirmMessage}>
              You have unsaved changes in your segment. Are you sure you want to
              close without saving?
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowConfirmModal(false)}
              >
                Keep Editing
              </button>
              <button
                className={styles.discardButton}
                onClick={handleModalClose}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <h2>Manage Users - {selectedSegmentName}</h2>
              {contexts?.length > 0 && (
                <div className={styles.contextDropdown}>
                  <button
                    className={`${styles.dropdownButton} ${
                      isContextLocked ? styles.locked : ""
                    }`}
                    onClick={() =>
                      !isContextLocked && setIsDropdownOpen(!isDropdownOpen)
                    }
                    disabled={isContextLocked}
                  >
                    {selectedContext?.name || "Select Context"}
                    {!isContextLocked && (
                      <FiChevronDown
                        className={isDropdownOpen ? styles.rotated : ""}
                      />
                    )}
                  </button>
                  {isDropdownOpen && !isContextLocked && (
                    <div className={styles.dropdownMenu}>
                      {contexts?.map((context) => (
                        <button
                          key={context.id}
                          className={styles.dropdownItem}
                          onClick={() => {
                            setSelectedContext({
                              id: context.id,
                              name: context.name,
                            });
                            setIsDropdownOpen(false);
                          }}
                        >
                          {context.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              className={styles.closeButton}
              onClick={() => {
                if (hasUnsavedChanges) {
                  setShowConfirmModal(true);
                } else handleModalClose();
              }}
            >
              <FiX />
            </button>
          </div>

          <div className={styles.modalBody}>
            {contexts?.length === 0 ? (
              <div className={styles.emptyContext}>
                <FiAlertCircle size={24} />
                <div className={styles.emptyContextContent}>
                  <h3>No Contexts Available</h3>
                  <p>
                    You need to create a context before adding users to
                    segments.
                  </p>
                  <button
                    className={styles.createContextButton}
                    onClick={() => setShowCreateContext(true)}
                  >
                    <FiPlus /> Create Context
                  </button>
                </div>
              </div>
            ) : !selectedContext ? (
              <div className={styles.contextNudge}>
                <FiAlertCircle />
                Please select a context to start adding users
              </div>
            ) : (
              <>
                <div className={styles.inputSection}>
                  <div className={styles.userInput}>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder={`Add ${selectedContext?.name} (comma-separated)`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddUsers();
                        }
                      }}
                    />
                    <button
                      className={styles.addUserButton}
                      onClick={handleAddUsers}
                      disabled={!inputValue.trim()}
                    >
                      <FiPlus /> Add
                    </button>
                  </div>

                  <div className={styles.uploadSection}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleFileUpload}
                      style={{ display: "none" }}
                    />
                    <button
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FiUpload /> Upload CSV
                    </button>
                    <span className={styles.uploadHint}>
                      {` or upload a CSV/TXT file with ${selectedContext?.name} IDs`}
                    </span>
                  </div>
                </div>

                {!!duplicateValues.length && (
                  <div className={styles.errorNudge}>
                    <FiAlertCircle />
                    {duplicateValues.length === 1
                      ? `${selectedContext?.name} "${duplicateValues[0]}" already exists`
                      : `${selectedContext?.name} ${duplicateValues
                          .map((id) => `"${id}"`)
                          .join(", ")} already exist`}
                  </div>
                )}

                <div ref={userListRef} className={styles.userList}>
                  <div className={styles.userTags}>
                    {contextValue.map((userId) => (
                      <span
                        key={userId}
                        className={`${styles.tag} ${
                          newValues.includes(userId)
                            ? styles.newTag
                            : styles.savedTag
                        }`}
                      >
                        {userId}
                        <button
                          onClick={() => handleRemoveUser(userId)}
                          className={styles.removeTag}
                        >
                          <FiX />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {removedValues.length > 0 && (
                  <div className={styles.removedSection}>
                    <div className={styles.removedHeader}>
                      <h4>Removed {selectedContext?.name}</h4>
                      <span className={styles.removedCount}>
                        {removedValues.length}{" "}
                        {removedValues.length === 1 ? "item" : "items"}
                      </span>
                    </div>
                    <div className={styles.removedTags}>
                      {removedValues.map((userId) => (
                        <span
                          key={userId}
                          className={`${styles.tag} ${styles.removedTag}`}
                        >
                          {userId}
                          <button
                            onClick={() => handleRestoreValue(userId)}
                            className={styles.restoreTag}
                            title="Restore"
                          >
                            <FiRotateCcw />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button
              className={styles.saveButton}
              onClick={handleSaveUsers}
              disabled={!hasUnsavedChanges || !selectedContext}
            >
              <FiSave /> Save Changes
            </button>
          </div>
        </div>
      </div>

      {showCreateContext && (
        <CreateContextModal
          isOpen={showCreateContext}
          onClose={handleCreateContextClose}
          onSuccess={handleCreateContextSuccess}
        />
      )}
    </>
  );
};

export default ViewUserDetailsModal;
